import React,{useState,useEffect,useMemo,useRef} from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import './shop.css'

/* ------------------------------------------------------------------
   ICONS — stroke: currentColor, so they take the token colour of
   whatever control they sit in.
   ------------------------------------------------------------------ */
const IconPlus=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>)
const IconEdit=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>)
const IconX=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>)
const IconSearch=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>)
const IconCopy=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>)
const IconCheck=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12.5l5 5L20 6.5"/></svg>)
const IconOut=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 4h6v6"/><path d="M20 4 10 14"/><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/></svg>)

/* ------------------------------------------------------------------
   THEME TOKENS FOR THE DATA GRID
   react-data-table-component styles through a JS object, so it cannot
   read CSS variables itself. We read them off :root and re-read on any
   theme change, which keeps the grid in step with the rest of the app.
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{ out[name]=String(styles.getPropertyValue(name)||'').trim() })
  return out
}

function useTableStyles(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return useMemo(()=>{
    const t=tokens
    return {
      table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
      tableWrapper:{ style:{ display:'block' } },
      head:{ style:{ color:t['--ink-3'] } },
      headRow:{
        style:{
          backgroundColor:t['--panel-raise'],
          color:t['--ink-3'],
          minHeight:'42px',
          borderBottomWidth:'1px',
          borderBottomColor:t['--line'],
          borderBottomStyle:'solid'
        }
      },
      headCells:{
        style:{
          paddingLeft:'12px',
          paddingRight:'12px',
          fontFamily:t['--mono'],
          fontSize:'10px',
          fontWeight:600,
          letterSpacing:'0.08em',
          textTransform:'uppercase',
          color:t['--ink-3']
        }
      },
      rows:{
        style:{
          backgroundColor:t['--panel'],
          color:t['--ink'],
          fontFamily:t['--sans'],
          fontSize:t['--step-0']||'0.875rem',
          minHeight:'54px',
          '&:not(:last-of-type)':{
            borderBottomWidth:'1px',
            borderBottomStyle:'solid',
            borderBottomColor:t['--line-soft']
          }
        },
        highlightOnHoverStyle:{
          backgroundColor:t['--accent-wash'],
          color:t['--ink'],
          borderBottomColor:t['--line-soft'],
          outlineWidth:'0px',
          outlineColor:t['--panel'],
          transitionDuration:'0.15s',
          transitionProperty:'background-color'
        }
      },
      cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
      noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'], padding:'26px' } },
      progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
    }
  },[tokens])
}

/* ------------------------------------------------------------------
   CLIPBOARD
   navigator.clipboard needs a secure context; the admin is often opened
   over plain http on the office LAN, so a selection fallback is kept.
   ------------------------------------------------------------------ */
const copyText=(text)=>{
  const value=String(text===undefined||text===null?'':text)
  if (!value) return Promise.reject(new Error('empty'))

  if (navigator.clipboard&&navigator.clipboard.writeText){
    return navigator.clipboard.writeText(value)
  }

  return new Promise((resolve,reject)=>{
    try{
      const holder=document.createElement('textarea')
      holder.value=value
      holder.setAttribute('readonly','')
      holder.style.position='fixed'
      holder.style.top='-1000px'
      holder.style.opacity='0'
      document.body.appendChild(holder)
      holder.select()
      const ok=document.execCommand('copy')
      document.body.removeChild(holder)
      if (ok) resolve()
      else reject(new Error('denied'))
    }catch(err){
      reject(err)
    }
  })
}

/**
 * A value the operator has to hand to someone else. It stays fully
 * selectable, copies on one click and says out loud what happened.
 */
function Copyable({value,label,openable,emptyText,compact}){
  const [state,setState]=useState('idle')
  const timer=useRef(null)
  const text=String(value===undefined||value===null?'':value)
  const has=text.trim().length>0

  useEffect(()=>{
    setState('idle')
  },[text])

  useEffect(()=>()=>{ if (timer.current) window.clearTimeout(timer.current) },[])

  const flash=(next)=>{
    setState(next)
    if (timer.current) window.clearTimeout(timer.current)
    timer.current=window.setTimeout(()=>setState('idle'),2600)
  }

  const run=()=>{
    if (!has) return
    copyText(text).then(()=>flash('done')).catch(()=>flash('fail'))
  }

  const say=state==='done'
    ? 'Αντιγράφηκε στο πρόχειρο.'
    : state==='fail'
      ? 'Η αντιγραφή απέτυχε — επιλέξτε το κείμενο και αντιγράψτε το χειροκίνητα.'
      : ''

  return (
    <div className={compact?'sh-copy-cell':undefined}>
      <div className="sh-copy" data-state={state}>
        <input
          className="sh-copy-v"
          type="text"
          readOnly
          value={has?text:(emptyText||'')}
          data-empty={has?'0':'1'}
          aria-label={label||'Σύνδεσμος'}
          onFocus={(e)=>e.target.select()}
        />
        <button
          type="button"
          className="sh-copy-btn"
          onClick={run}
          disabled={!has}
          aria-label={(label?label+': ':'')+'αντιγραφή'}
        >
          {state==='done'?<IconCheck/>:<IconCopy/>}
          <span>{state==='done'?'Αντιγράφηκε':'Αντιγραφή'}</span>
        </button>
        {openable&&has
          ?<a className="sh-copy-open" href={text} target="_blank" rel="noreferrer" aria-label="Άνοιγμα σε νέα καρτέλα"><IconOut/></a>
          :null}
      </div>
      <div className="sh-copy-say" data-tone={state==='done'?'ok':state==='fail'?'crit':undefined} role="status" aria-live="polite">{say}</div>
    </div>
  )
}

/* ------------------------------------------------------------------
   DRAWER — the product editor.

   The editor used to be a centred modal. A product form is long
   (title, sport, price, duration, countries, a rich-text description
   and two sale links), so a centred card either grew taller than the
   viewport or scrolled the page behind it. A right-side drawer is the
   better container for a long single-column form: it is full height,
   it scrolls inside itself, and the table it was opened from stays
   visible on the left.

   Nothing about the form changed — same fields, same handlers, same
   axios calls. Only the box around them.

     - slides in from the right, 520px wide, full height
     - its body owns the scroll; the header stays put
     - backdrop click and Escape close it
     - focus moves into the drawer on open and returns to the control
       that opened it on close, and Tab is kept inside while it is open
     - under 560px it goes full-bleed, because on a phone a drawer is
       a screen, not a panel
   ------------------------------------------------------------------ */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

function Drawer({open,title,eyebrow,subtitle,onClose,children}){
  const panelRef=useRef(null)

  /* Remember the control that opened us so focus can go home, and put
     focus on the drawer itself so its label is announced. */
  useEffect(()=>{
    if (!open) return undefined

    const opener=(document.activeElement&&document.activeElement!==document.body)
      ? document.activeElement
      : null

    if (panelRef.current) panelRef.current.focus()

    /* The page behind a modal surface must not scroll under it. On a phone the
       drawer is full-bleed, so a drag anywhere outside .sh-drawer-body was
       moving the products table underneath instead of doing nothing. This is
       the same lock the people cluster's Modal already takes. */
    const prevOverflow=document.body.style.overflow
    document.body.style.overflow='hidden'

    return ()=>{
      document.body.style.overflow=prevOverflow
      if (opener&&typeof opener.focus==='function'&&document.contains(opener)) opener.focus()
    }
  },[open])

  /* Escape closes. Tab cycles inside — a modal surface that leaks focus
     to the table behind it is a modal surface in name only. */
  useEffect(()=>{
    if (!open) return undefined

    const onKey=(e)=>{
      if (e.key==='Escape'){ onClose(); return }
      if (e.key!=='Tab') return

      const panel=panelRef.current
      if (!panel) return

      const list=Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE))
        .filter(el=>el===document.activeElement||el.offsetWidth>0||el.offsetHeight>0)
      if (list.length===0){ e.preventDefault(); panel.focus(); return }

      const first=list[0]
      const last=list[list.length-1]
      const here=document.activeElement

      if (e.shiftKey){
        if (here===first||here===panel||!panel.contains(here)){ e.preventDefault(); last.focus() }
      }else if (here===last){
        e.preventDefault(); first.focus()
      }
    }

    document.addEventListener('keydown',onKey)
    return ()=>document.removeEventListener('keydown',onKey)
  },[open,onClose])

  if (!open) return null

  return (
    <div className="sh-drawer-back" onMouseDown={(e)=>{ if (e.target===e.currentTarget) onClose() }}>
      <aside
        className="sh-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="sh-drawer-head">
          <div className="sh-modal-headline">
            {eyebrow?<span className="sh-modal-eyebrow">{eyebrow}</span>:null}
            <h2 className="sh-modal-title">{title}</h2>
            {subtitle?<div className="sh-modal-sub">{subtitle}</div>:null}
          </div>
          <button type="button" className="sh-modal-x" onClick={onClose} aria-label="Κλείσιμο"><IconX/></button>
        </div>
        <div className="sh-drawer-body">{children}</div>
      </aside>
    </div>
  )
}

const money=(value)=>{
  const n=Number(value)
  if (isNaN(n)) return String(value===undefined||value===null?'':value)
  return (n%1===0?String(n):n.toFixed(2))+' €'
}

export default function EShopProducts() {
    const [data,setData]=useState([])
    const [isLoading,setIsLoading]=useState(true)
    const [show, setShow] = useState(false);
    const [record,setRecord]=useState({})
    const [url,setUrl]=useState('')
    const [paymentLink,setPaymentLink]=useState('')
    const [sports,setSports]=useState([])

    /* inline feedback — this is what replaced alert() / window.confirm() */
    const [note,setNote]=useState(null)
    const [formNote,setFormNote]=useState(null)
    const [askDelete,setAskDelete]=useState(false)
    const [payBusy,setPayBusy]=useState(false)
    const [q,setQ]=useState('')

    const tableStyles=useTableStyles()

    /* The link the manager is actually sold through. Built exactly as before. */
    const checkoutLink=(uuid)=>(uuid?url+'/#/checkout/'+uuid:'')

    const columns = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width:'56px'
        },
        {
            name: 'Όνομα',
            selector: row =>  row.Title,
            cell: row => <span className="sh-cell-strong">{row.Title}</span>,
            sortable: true,
            minWidth:'180px'
        },
        {
            name: 'Άθλημα',
            selector: row =>  row.SportName,
            sortable: true,
            minWidth:'130px'
        },
        {
            name: 'Συνδρομή',
            selector: row =>  Number(row.LicenceDurationMonths)||0,
            cell: row => <span>{row.LicenceDurationMonths+' months'}</span>,
            sortable: true,
            width:'120px'
        },
        {
            name: 'Τιμή',
            selector: row =>  Number(row.Price)||0,
            cell: row => <span className="num sh-cell-strong">{money(row.Price)}</span>,
            sortable: true,
            width:'110px'
        },
        {
            name: 'EShop Link',
            cell: row => (
                <Copyable
                    compact
                    openable
                    value={checkoutLink(row.UUID)}
                    label={'EShop link — '+row.Title}
                    emptyText="Ορίστε πρώτα το EShop Url"
                />
            ),
            sortable: false,
            minWidth:'320px'
        },
        {
            name: '',
            cell: row => (
                <button type="button" className="sh-icon-btn" onClick={()=>showModal(row.id)} aria-label={'Επεξεργασία — '+row.Title}>
                    <IconEdit/>
                </button>
            ),
            sortable: false,
            width:'70px',
            right:true
        }
    ]

    const handleClose = () => setShow(false);

    useEffect(()=>{
        loadData()
    },[])


    const loadSports=()=>{
        axios.get(process.env.REACT_APP_API_SERVER+'admin/sports',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
          setSports(res.data.results)
        }).catch(err=>{
            console.log(err)
        })
      }


    const productpaymentLink=(UUID)=>{
        setPayBusy(true)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/eshopproducts/paymentlink/'+UUID,{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
            if (res.data.err==0){
                setPaymentLink(res.data.url)
            }else{

                setPaymentLink('ERROR: '+res.data.msg.code)
            }
            setPayBusy(false)
        }).catch(err=>{
            setPayBusy(false)
            setFormNote({tone:'crit',text:'Το Stripe payment link δεν επιστράφηκε. Δοκιμάστε ξανά.'})
            console.log(err)
        })
      }


      const loadData=()=>{
        setIsLoading(true)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/eshopproducts',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
          setData(res.data.results)
          loadSettings()
          loadSports()
          setIsLoading(false)
        }).catch(err=>{
            setIsLoading(false)
            setNote({tone:'crit',text:'Τα προϊόντα δεν φορτώθηκαν. Ελέγξτε τη σύνδεση και δοκιμάστε ξανά.'})
            console.log(err)
        })
      }

      const loadSettings=()=>{
        axios.get(process.env.REACT_APP_API_SERVER+'admin/eshopsettings',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
          setUrl(res.data.results[0].EShopUrl)
        }).catch(err=>{
            console.log(err)
        })
      }

      const showModal=(id)=>{
        setPaymentLink('')
        setFormNote(null)
        setAskDelete(false)
        if (id==0){
            setRecord({ModalTitle:'ΝΕΑ ΕΓΓΡΑΦΗ', id:0,Title:'',Price:0,Descr:'',NumberOfCountries:0,SportID:0,LicenceDurationMonths:0,SportName:'',TopListCreation:0})
            setShow(true)
        }else{
            const recObj=data.filter(item=>item.id==id)[0]
            if (!recObj) return

            setRecord({ModalTitle:recObj.Title,NumberOfCountries:recObj.NumberOfCountries, Descr:recObj.Descr,UUID:recObj.UUID,id:recObj.id,Title:recObj.Title,Descr:recObj.Descr,Price:recObj.Price,SportID:recObj.SportID,LicenceDurationMonths:recObj.LicenceDurationMonths,SportName:recObj.SportName,TopListCreation:recObj.TopListCreation})
            setShow(true)
        }

      }

      const save=(e)=>{
        e.preventDefault()

        axios.post(process.env.REACT_APP_API_SERVER+'admin/eshopproducts',record,{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                }
            })
          .then(res=>{
            if (res.data.err==0){
                loadData()
                setShow(false)
                setNote({tone:'ok',text:res.data.msg})
            }else{
                setFormNote({tone:'crit',text:res.data.msg})
            }
        }).catch(err=>{
            setFormNote({tone:'crit',text:'Η αποθήκευση απέτυχε. Δοκιμάστε ξανά.'})
            console.log(err)
        })
      }

      const deleteRecord=()=>{
            axios.delete(process.env.REACT_APP_API_SERVER+'admin/eshopproducts/'+record.id,{
                headers: {
                    'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                    }
                })
              .then(res=>{
                if (res.data.err==0){
                    loadData()
                    setShow(false)
                    setAskDelete(false)
                    setNote({tone:'ok',text:res.data.msg})
                }else{
                    setFormNote({tone:'crit',text:res.data.msg})
                }
            }).catch(err=>{
                setFormNote({tone:'crit',text:'Η διαγραφή απέτυχε. Δοκιμάστε ξανά.'})
                console.log(err)
            })
      }

      const sportsList=sports.map(item=>{
        return(
            <option key={item.SportID} value={item.SportID}>{item.SportName}</option>
        )
      })

      const changeSport=(id)=>{
        const hit=sports.filter(item=>item.SportID==id)[0]
        setRecord({...record,SportID:id,SportName:hit?hit.SportName:''})
      }

      const rows=Array.isArray(data)?data:[]
      const needle=q.trim().toLowerCase()
      const shown=needle
        ? rows.filter(item=>(String(item.Title||'')+' '+String(item.SportName||'')).toLowerCase().indexOf(needle)>=0)
        : rows

      const linkReady=String(url||'').trim().length>0

    return (
        <div className="sh-page">

            {note
              ? <div className="sh-note" data-tone={note.tone} role="status">
                  <span className="sh-note-text">{note.text}</span>
                  <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setNote(null)}><IconX/></button>
                </div>
              : null}

            {!isLoading&&!linkReady
              ? <div className="sh-note" data-tone="info" role="status">
                  <span className="sh-note-text">
                    Το <b>EShop Url</b> δεν έχει οριστεί στα EShop Settings, οπότε οι σύνδεσμοι checkout
                    δεν μπορούν να παραχθούν. Ορίστε το και επιστρέψτε εδώ.
                  </span>
                </div>
              : null}

            <Panel
              title="EShop Products"
              subtitle={isLoading
                ? 'Φόρτωση…'
                : rows.length===0
                  ? 'Κανένα προϊόν'
                  : rows.length+(rows.length===1?' προϊόν':' προϊόντα')+' προς πώληση'}
              actions={
                <div className="sh-tools">
                  {rows.length>0
                    ? <span className="sh-search">
                        <IconSearch/>
                        <input
                          className="sh-search-input"
                          type="search"
                          value={q}
                          placeholder="Αναζήτηση"
                          aria-label="Αναζήτηση προϊόντος"
                          onChange={(e)=>setQ(e.target.value)}
                        />
                      </span>
                    : null}
                  <Btn variant="accent" onClick={()=>showModal(0)}><IconPlus/><span>Νέο προϊόν</span></Btn>
                </div>
              }
            >
              {isLoading
                ? <div className="sh-sk" aria-hidden="true">
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                  </div>
                : rows.length===0
                  ? <div className="sh-empty">
                      <div className="sh-empty-t">Κανένα προϊόν ακόμη</div>
                      <div className="sh-empty-s">
                        Κάθε προϊόν είναι ένα πακέτο συνδρομής που αγοράζει ο manager.
                        Μόλις δημιουργηθεί, παίρνει τον δικό του σύνδεσμο checkout.
                      </div>
                      <div className="sh-empty-a">
                        <Btn variant="accent" onClick={()=>showModal(0)}><IconPlus/><span>Νέο προϊόν</span></Btn>
                      </div>
                    </div>
                  : shown.length===0
                    ? <div className="sh-empty">
                        <div className="sh-empty-t">Κανένα προϊόν δεν ταιριάζει</div>
                        <div className="sh-empty-s">Καθαρίστε την αναζήτηση για να δείτε και τα {rows.length} προϊόντα.</div>
                        <div className="sh-empty-a"><Btn onClick={()=>setQ('')}>Καθαρισμός</Btn></div>
                      </div>
                    : <>
                        <div className="sh-count">
                          <span className="sh-count-n num">{shown.length}</span>
                          <span>{needle?'αποτελέσματα αναζήτησης':'ενεργά προϊόντα στο eshop'}</span>
                        </div>
                        <div className="sh-table">
                          <DataTable
                            columns={columns}
                            data={shown}
                            customStyles={tableStyles}
                            highlightOnHover
                            persistTableHead
                            noDataComponent={<div className="sh-empty-s">Κανένα προϊόν.</div>}
                          />
                        </div>
                      </>}
            </Panel>

            <Drawer
              open={show}
              eyebrow={record.id>0?'Επεξεργασία προϊόντος':'Νέο προϊόν'}
              title={record.ModalTitle}
              subtitle={record.id>0?'Οι αλλαγές ισχύουν αμέσως για νέες αγορές.':'Θα δημιουργηθεί νέος σύνδεσμος checkout.'}
              onClose={handleClose}
            >
              {formNote
                ? <div className="sh-note" data-tone={formNote.tone} role="alert">
                    <span className="sh-note-text">{formNote.text}</span>
                    <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setFormNote(null)}><IconX/></button>
                  </div>
                : null}

              <form className="sh-form" onSubmit={save}>

                <fieldset className="sh-fieldset">
                  <legend className="sh-legend">Στοιχεία προϊόντος</legend>

                  <div className="sh-field">
                    <label className="sh-label" htmlFor="pr-title">Τίτλος</label>
                    <input
                      id="pr-title"
                      className="sh-input"
                      type="text"
                      maxLength={250}
                      required
                      value={record.Title}
                      onChange={(e)=>setRecord({...record,Title:e.target.value})}
                    />
                  </div>

                  <div className="sh-pair">
                    <div className="sh-field">
                      <label className="sh-label" htmlFor="pr-sport">Άθλημα</label>
                      <select
                        id="pr-sport"
                        className="sh-select"
                        required
                        value={record.SportID}
                        onChange={(e)=>changeSport(e.target.value)}
                      >
                        <option value=''>[ΕΠΙΛΕΞΤΕ ΑΘΛΗΜΑ]</option>
                        {sportsList}
                      </select>
                    </div>

                    <div className="sh-field">
                      <label className="sh-label" htmlFor="pr-price">Τιμή <span className="sh-label-note">σε €</span></label>
                      <input
                        id="pr-price"
                        className="sh-input"
                        type="number"
                        min={0}
                        required
                        value={record.Price}
                        onChange={(e)=>setRecord({...record,Price:e.target.value})}
                      />
                    </div>
                  </div>
                </fieldset>

                <fieldset className="sh-fieldset">
                  <legend className="sh-legend">Τι αγοράζει ο manager</legend>

                  <div className="sh-pair">
                    <div className="sh-field">
                      <label className="sh-label" htmlFor="pr-months">Διάρκεια Συνδρομής σε Μήνες</label>
                      <input
                        id="pr-months"
                        className="sh-input"
                        type="number"
                        min={0}
                        step={1}
                        required
                        value={record.LicenceDurationMonths}
                        onChange={(e)=>setRecord({...record,LicenceDurationMonths:e.target.value})}
                      />
                    </div>

                    <div className="sh-field">
                      <label className="sh-label" htmlFor="pr-countries">Σε πόσες χώρες θα έχει πρόσβαση</label>
                      <input
                        id="pr-countries"
                        className="sh-input"
                        type="number"
                        min={0}
                        step={1}
                        required
                        value={record.NumberOfCountries}
                        onChange={(e)=>setRecord({...record,NumberOfCountries:e.target.value})}
                      />
                      <div className="sh-hint">0 για όλες τις χώρες.</div>
                    </div>
                  </div>

                  <div className="sh-field">
                    <label className="sh-label" htmlFor="pr-toplist">Δημιουργία Top List</label>
                    <select
                      id="pr-toplist"
                      className="sh-select"
                      value={record.TopListCreation}
                      onChange={(e)=>setRecord({...record,TopListCreation:e.target.value})}
                    >
                      <option value='0'>ΟΧΙ</option>
                      <option value='1'>ΝΑΙ</option>
                    </select>
                  </div>

                  <div className="sh-field">
                    <span className="sh-label">Περιγραφή</span>
                    <div className="sh-editor">
                      <CKEditor
                        editor={ ClassicEditor }
                        data={record.Descr}

                        onChange={ ( event, editor ) => {
                            const data = editor.getData();
                            setRecord({...record,Descr:data.replace("'","`")})
                        } }

                      />
                    </div>
                  </div>
                </fieldset>

                {record.id>0 &&
                <fieldset className="sh-fieldset">
                  <legend className="sh-legend">Σύνδεσμοι πώλησης</legend>

                  <div className="sh-field">
                    <span className="sh-label">EShop Link</span>
                    <div className="sh-hint">Αυτόν τον σύνδεσμο δίνετε στον manager για να αγοράσει το προϊόν.</div>
                    <Copyable
                      openable
                      value={checkoutLink(record.UUID)}
                      label="EShop link"
                      emptyText="Ορίστε πρώτα το EShop Url στα EShop Settings"
                    />
                  </div>

                  <div className="sh-field">
                    <span className="sh-label">Stripe Payment Link</span>
                    <div className="sh-hint">
                      Δεν είναι αποθηκευμένο στοιχείο του προϊόντος. Κάθε φορά που πατάτε
                      «Payment Link», το Stripe δημιουργεί <b>νέα συνεδρία checkout</b> από τον
                      τίτλο και την τιμή του προϊόντος και επιστρέφει έναν φρέσκο σύνδεσμο που
                      μπορείτε να στείλετε για πληρωμή εκτός eshop. Προϊόν χωρίς σύνδεσμο εδώ
                      δεν έχει κάποιο πρόβλημα — απλώς δεν έχει ζητηθεί ακόμη.
                    </div>
                    {String(paymentLink||'').indexOf('ERROR:')===0
                      ? <div className="sh-note" data-tone="crit" role="alert">
                          <span className="sh-note-text">{paymentLink}</span>
                        </div>
                      : <Copyable
                          openable
                          value={paymentLink}
                          label="Stripe payment link"
                          emptyText="Δεν έχει παραχθεί ακόμη"
                        />}
                    <div className="sh-actions">
                      <Btn onClick={()=>productpaymentLink(record.UUID)} disabled={payBusy}>
                        {payBusy?'Δημιουργία…':'Payment Link'}
                      </Btn>
                    </div>
                  </div>
                </fieldset>
                }

                {askDelete
                  ? <div className="sh-confirm" role="group" aria-label="Επιβεβαίωση διαγραφής">
                      <div className="sh-confirm-t">Διαγραφή του «{record.Title}»;</div>
                      <div className="sh-confirm-s">
                        Το προϊόν φεύγει από το eshop και ο σύνδεσμος checkout του παύει να λειτουργεί.
                        Οι υπάρχουσες συνδρομές δεν επηρεάζονται. Η ενέργεια δεν αναιρείται.
                      </div>
                      <div className="sh-actions">
                        <Btn onClick={()=>setAskDelete(false)}>Άκυρο</Btn>
                        <span className="sh-actions-end">
                          <Btn className="sh-btn-crit" onClick={deleteRecord}>Οριστική διαγραφή</Btn>
                        </span>
                      </div>
                    </div>
                  : <div className="sh-actions">
                      {record.id>0
                        ? <Btn className="sh-btn-crit" onClick={()=>setAskDelete(true)}>Διαγραφή</Btn>
                        : null}
                      <span className="sh-actions-end">
                        <Btn onClick={handleClose}>Άκυρο</Btn>
                        <Btn variant="accent" type="submit">Αποθήκευση</Btn>
                      </span>
                    </div>}

              </form>
            </Drawer>
        </div>
    )
}
